import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Text,
  VStack,
  HStack,
  useToast,
  Tooltip,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Badge,
  Input,
  InputGroup,
  InputRightAddon,
  Stack,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { Reminder, TriggerRelation, ReminderAction } from '../../../types/task';
import { MdAdd, MdDelete } from 'react-icons/md';
import { format, parseISO, isValid } from 'date-fns';
import { toZonedTime, formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import {
  getAllReminderMethods,
  ReminderMethodType,
  ReminderMethodConfig
} from '../../../types/notificationMapping';

// 使用统一的提醒方式配置
const REMINDER_METHODS = getAllReminderMethods();

const TRIGGER_TYPE_OPTIONS = [
  { value: 'RELATIVE', label: '相对时间' },
  { value: 'ABSOLUTE', label: '指定时间' },
];

const TRIGGER_RELATION_OPTIONS = [
  { value: TriggerRelation.START, label: '开始时间' },
  { value: TriggerRelation.CALCULATED_END, label: '计算结束时间' },
  { value: TriggerRelation.DUE, label: '截止时间' },
];

const TIME_UNIT_OPTIONS = [
  { value: 'minutes', label: '分钟', factor: 60 },
  { value: 'hours', label: '小时', factor: 3600 },
  { value: 'days', label: '天', factor: 86400 },
];

const secondsToValueUnit = (seconds: number): { value: number; unit: 'minutes' | 'hours' | 'days' } => {
  const absSeconds = Math.abs(seconds);
  if (absSeconds >= 86400 && absSeconds % 86400 === 0) {
    return { value: absSeconds / 86400, unit: 'days' };
  }
  if (absSeconds >= 3600 && absSeconds % 3600 === 0) {
    return { value: absSeconds / 3600, unit: 'hours' };
  }
  // Default to minutes, even for less than 60 seconds or non-round numbers
  return { value: Math.round(absSeconds / 60), unit: 'minutes' };
};

const valueUnitToSeconds = (value: number, unit: string, isBefore: boolean): number => {
  const unitInfo = TIME_UNIT_OPTIONS.find(u => u.value === unit);
  const factor = unitInfo ? unitInfo.factor : 60;
  const seconds = value * factor;
  return isBefore ? -seconds : seconds;
};

const formatAbsoluteTimeForInput = (isoString?: string): string => {
  if (!isoString) return '';
  try {
    const date = parseISO(isoString);
    if (!isValid(date)) return '';
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const localDate = toZonedTime(date, userTimeZone);
    return format(localDate, "yyyy-MM-dd'T'HH:mm");
  } catch (error) {
    console.error("Error formatting absolute date for input:", error);
    return '';
  }
};

const parseInputToAbsoluteTime = (localString: string): string | undefined => {
  if (!localString) return undefined;
  try {
    const localDate = new Date(localString);
    if (!isValid(localDate)) return undefined;
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const utcDate = fromZonedTime(localDate, userTimeZone);
    return utcDate.toISOString().replace(/\.\d{3}Z$/, 'Z');
  } catch (error) {
    console.error("Error parsing local input to absolute UTC:", error);
    return undefined;
  }
};

const getReminderDescription = (reminder: Reminder, t: Function): string => {
  const { trigger } = reminder;

  if (trigger.type === 'ABSOLUTE') {
    if (!trigger.absoluteTime) return t('v0/tasks:reminders.types.absolute', '指定时间');
    try {
      const date = parseISO(trigger.absoluteTime);
      if (!isValid(date)) return t('v0/tasks:reminders.invalidAbsoluteTime', '无效的指定时间');
      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return formatInTimeZone(date, userTimeZone, 'yyyy-MM-dd HH:mm') + ` (${userTimeZone.split('/').pop()?.replace('_', ' ') || userTimeZone})`;
    } catch {
      return t('v0/tasks:reminders.invalidAbsoluteTime', '无效的指定时间');
    }
  } else if (trigger.type === 'RELATIVE') {
    if (trigger.offsetSeconds === undefined || trigger.related === undefined) return t('v0/tasks:reminders.incompleteRelative', '未完成的相对时间');

    let relationText = '';
    switch (trigger.related) {
      case TriggerRelation.START:
        relationText = t('v0/tasks:reminders.relativeToOptions.START', '开始时间');
        break;
      case TriggerRelation.CALCULATED_END:
        relationText = t('v0/tasks:reminders.relativeToOptions.CALCULATED_END', '计算结束时间');
        break;
      case TriggerRelation.DUE:
        relationText = t('v0/tasks:reminders.relativeToOptions.DUE', '截止时间');
        break;
      default:
        relationText = '?';
    }

    const offset = trigger.offsetSeconds;
    const isBefore = offset < 0;
    const absOffset = Math.abs(offset);
    const { value, unit } = secondsToValueUnit(absOffset);
    const unitText = TIME_UNIT_OPTIONS.find(u => u.value === unit)?.label || '?';
    const directionText = isBefore
      ? t('v0/tasks:reminders.direction.before', '前')
      : t('v0/tasks:reminders.direction.after', '后');

    return `${relationText} ${directionText} ${value}${unitText}`;
  } else {
    return t('v0/tasks:reminders.unknownType', '未知提醒类型');
  }
};

interface RemindersEditorProps {
  reminders: Reminder[];
  onChange: (reminders: Reminder[]) => void;
  isDisabled?: boolean;
}

export const RemindersEditor: React.FC<RemindersEditorProps> = ({
  reminders,
  onChange,
  isDisabled = false,
}) => {
  const { t } = useTranslation(['v0/tasks', 'v0/common']);
  const toast = useToast();

  const handleAddReminder = () => {
    const newReminder: Reminder = {
      action: 'DISPLAY',
      trigger: {
        type: 'RELATIVE',
        related: TriggerRelation.START,
        offsetSeconds: -300,
      },
      methods: ['桌面通知'],
    };
    onChange([...reminders, newReminder]);
  };

  const handleDeleteReminder = (index: number) => {
    const newReminders = [...reminders];
    newReminders.splice(index, 1);
    onChange(newReminders);
  };

  const updateReminder = (index: number, changes: Partial<Reminder> | { trigger: Partial<Reminder['trigger']> }) => {
    const newReminders = [...reminders];
    const currentReminder = { ...newReminders[index] }; // Create a shallow copy to modify safely

    if ('trigger' in changes && typeof changes.trigger === 'object') {
      // Deep merge trigger changes
      currentReminder.trigger = { ...currentReminder.trigger, ...changes.trigger };
    } else {
      // Merge top-level changes
      Object.assign(currentReminder, changes);
    }

    const currentTrigger = currentReminder.trigger; // Use the modified trigger
    if (currentTrigger.type === 'ABSOLUTE') {
       if (!currentTrigger.absoluteTime) {
          currentTrigger.absoluteTime = new Date().toISOString().replace(/\.\d{3}Z$/, 'Z');
       }
       delete currentTrigger.related;
       delete currentTrigger.offsetSeconds;
    } else { // RELATIVE
       if (currentTrigger.related === undefined || !Object.values(TriggerRelation).includes(currentTrigger.related)) {
           currentTrigger.related = TriggerRelation.START;
       }
       if (currentTrigger.offsetSeconds === undefined) {
           currentTrigger.offsetSeconds = -300;
       }
       delete currentTrigger.absoluteTime;
    }

    // Update the reminder in the array
    newReminders[index] = currentReminder;
    onChange(newReminders);
  };

  const handleMethodToggle = (index: number, method: ReminderMethodConfig) => {
    const currentReminder = reminders[index];
    const currentMethods = currentReminder.methods;
    let updatedMethods: ReminderMethodType[];

    if (currentMethods.includes(method.value)) {
      if (currentMethods.length > 1) {
        updatedMethods = currentMethods.filter(m => m !== method.value);
      } else {
        toast({ title: t('v0/tasks:reminders.needOneMethod', '至少需要一种提醒方式'), status: 'warning', duration: 2000, isClosable: true });
        return;
      }
    } else {
      updatedMethods = [...currentMethods, method.value];
    }

    const action: ReminderAction = 'DISPLAY'; // Keep action simple for now
    updateReminder(index, { methods: updatedMethods, action }); // Update methods and action together
  };

  return (
    <Box w="100%">
      <Flex justify="space-between" align="center" mb={4}>
        <FormLabel m={0}>{t('v0/tasks:reminders.title', '提醒')}</FormLabel>
        <Tooltip label={t('v0/tasks:reminders.addReminder', '添加提醒')}>
          <IconButton aria-label={t('v0/tasks:reminders.addReminder', '添加提醒')} icon={<MdAdd />} size="sm" onClick={handleAddReminder} isDisabled={isDisabled} colorScheme="green" />
        </Tooltip>
      </Flex>

      {reminders.length === 0 ? (
        <Text fontSize="sm" color="gray.500">{t('v0/tasks:reminders.noReminders', '没有设置提醒')}</Text>
      ) : (
        <VStack spacing={4} align="stretch">
          {reminders.map((reminder, index) => {
            const trigger = reminder.trigger;
            const isRelative = trigger.type === 'RELATIVE';
            const relativeOffset = trigger.offsetSeconds || 0;
            const isBefore = relativeOffset < 0;
            const { value: timeValue, unit: timeUnit } = secondsToValueUnit(relativeOffset);

            return (
              <Box key={index} p={3} borderWidth="1px" borderRadius="md" borderColor="gray.200" bgColor="gray.50">
                <Flex justify="space-between" mb={3}>
                  <FormLabel fontSize="sm" mb={0}>{t('v0/tasks:reminders.reminderNum', `提醒 ${index + 1}`)}</FormLabel>
                  <Tooltip label={t('v0/tasks:reminders.deleteReminder', '删除提醒')}>
                    <IconButton aria-label={t('v0/tasks:reminders.deleteReminder', '删除提醒')} icon={<MdDelete />} size="xs" onClick={() => handleDeleteReminder(index)} isDisabled={isDisabled} colorScheme="red" variant="ghost" />
                  </Tooltip>
                </Flex>

                <Stack direction={['column', 'row']} spacing={4} mb={3}>
                  <FormControl flex={1}>
                    <FormLabel fontSize="xs">{t('v0/tasks:reminders.triggerType', '触发类型')}</FormLabel>
                    <Select value={trigger.type} onChange={(e) => updateReminder(index, { trigger: { type: e.target.value as 'RELATIVE' | 'ABSOLUTE' } })} isDisabled={isDisabled} size="sm">
                      {TRIGGER_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{t(`v0/tasks:reminders.triggerTypes.${option.value}`, option.label)}</option>
                      ))}
                    </Select>
                  </FormControl>

                  {isRelative && (
                    <>
                      <FormControl flex={1}>
                        <FormLabel fontSize="xs">{t('v0/tasks:reminders.relativeTo', '相对于')}</FormLabel>
                        <Select value={trigger.related || TriggerRelation.START} onChange={(e) => updateReminder(index, { trigger: { related: e.target.value as TriggerRelation } })} isDisabled={isDisabled} size="sm">
                          {TRIGGER_RELATION_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>{t(`v0/tasks:reminders.relativeToOptions.${option.value}`, option.label)}</option>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl flex={2}>
                        <FormLabel fontSize="xs">{t('v0/tasks:reminders.offset', '偏移量')}</FormLabel>
                        <HStack>
                          <Select value={isBefore ? 'before' : 'after'} onChange={(e) => updateReminder(index, { trigger: { offsetSeconds: valueUnitToSeconds(timeValue, timeUnit, e.target.value === 'before') } })} isDisabled={isDisabled} size="sm" w="80px">
                            <option value="before">{t('v0/tasks:reminders.direction.before', '前')}</option>
                            <option value="after">{t('v0/tasks:reminders.direction.after', '后')}</option>
                          </Select>
                          <NumberInput flex={1} value={timeValue} onChange={(_, value) => updateReminder(index, { trigger: { offsetSeconds: valueUnitToSeconds(value, timeUnit, isBefore) } })} min={0} isDisabled={isDisabled} size="sm">
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                          <Select flex={1} value={timeUnit} onChange={(e) => updateReminder(index, { trigger: { offsetSeconds: valueUnitToSeconds(timeValue, e.target.value, isBefore) } })} isDisabled={isDisabled} size="sm">
                            {TIME_UNIT_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </Select>
                        </HStack>
                      </FormControl>
                    </>
                  )}

                  {!isRelative && (
                    <FormControl flex={2}>
                      <FormLabel fontSize="xs">{t('v0/tasks:reminders.specificTime', '指定日期时间')}</FormLabel>
                      <Input
                        type="datetime-local"
                        value={formatAbsoluteTimeForInput(trigger.absoluteTime)}
                        onChange={(e) => updateReminder(index, { trigger: { absoluteTime: parseInputToAbsoluteTime(e.target.value) } })}
                        isDisabled={isDisabled}
                        size="sm"
                      />
                    </FormControl>
                  )}
                </Stack>

                <Box mb={3}>
                  <Badge colorScheme="blue" mb={1}>{t('v0/tasks:reminders.currentSetting', '当前设置')}</Badge>
                  <Text fontSize="sm">{getReminderDescription(reminder, t)}</Text>
                </Box>

                <FormControl>
                  <FormLabel fontSize="sm" mb={1}>{t('v0/tasks:reminders.methods', '提醒方式')}</FormLabel>
                  <Flex wrap="wrap" gap={3}>
                    {REMINDER_METHODS.map((method) => (
                      <Checkbox
                        key={method.id}
                        isChecked={reminder.methods.includes(method.value)}
                        onChange={() => handleMethodToggle(index, method)}
                        isDisabled={
                          isDisabled ||
                          !method.available ||
                          (reminder.methods.length === 1 && reminder.methods.includes(method.value))
                        }
                        colorScheme="blue"
                        size="sm"
                      >
                        <HStack spacing={1}>
                          <Text>{method.label}</Text>
                          {!method.available && (
                            <Badge colorScheme="gray" size="sm">{t('v0/common:common.comingSoon', '敬请期待')}</Badge>
                          )}
                        </HStack>
                      </Checkbox>
                    ))}
                  </Flex>
                </FormControl>
              </Box>
            );
          })}
        </VStack>
      )}

      <Box mt={4} fontSize="xs" color="gray.500">
        <Text>{t('v0/tasks:reminders.helpValarm', '提示：您可以设置相对于任务开始或结束时间的提醒，或指定一个绝对时间。')}</Text>
      </Box>
    </Box>
  );
};